import { useState, useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useMobileMenu } from '../hooks/useMobileMenu';
import { useCategories, preloadCategories } from '../hooks/useCategories';
import { useCreateAd, useAds, useAdDetails } from '../hooks/useAds';
import * as adService from '../services/adService';
import { useCategorySchema } from '../hooks/useCategorySchema';
import type { CategoryConfig, AdField } from '../config/postAdTypes';
import {
  ChevronRight,
  Upload,
  X,
  Check,
  AlertCircle,
  Image as ImageIcon,
  ArrowLeft,
  Sparkles,
  Search,
} from 'lucide-react';
import type { CreateAdRequest } from '../types/ad';

// Preload categories on module load for instant access
preloadCategories();

// Form fields are now fully dynamic from /api/categories/<uuid>/schema/
// No hardcoded field configurations needed - backend drives the form structure

interface FormData {
  [key: string]: string | number | boolean | string[] | File[];
}

interface FormErrors {
  [key: string]: string;
}

interface ImagePreview {
  id: string;
  file: File;
  preview: string;
}

export default function PostAdPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit') || undefined;
  const { mobileOpen, toggleMobile } = useMobileMenu();
  const { categories: apiCategories, isLoading: isLoadingCategories } = useCategories();
  const { isSubmitting, error: apiError, createAd } = useCreateAd();
  const { updateAd } = useAds();
  const { ad: editingAd, isLoading: isLoadingAd, refetch: refetchAd } = useAdDetails(editId);
  const { schema, fields: schemaFields, isLoading: isLoadingSchema, error: schemaError, fetchSchema, reset: resetSchema } = useCategorySchema();

  // Image-upload limits come from the schema (fallback to sensible defaults)
  const MAX_IMAGES = schema?.max_images ?? 5;
  const MAX_IMAGE_SIZE_MB = schema?.max_image_size_mb ?? 5;
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [subcategorySearch, setSubcategorySearch] = useState('');
  const [formData, setFormData] = useState<FormData>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize edit mode: prefill form and lock category on first load
  useEffect(() => {
    if (!editId || !editingAd || !apiCategories.length) return;

    // Determine category selections
    const leafId = typeof editingAd.category === 'string' ? editingAd.category : editingAd.category.id;
    const parentId = typeof editingAd.category === 'string'
      ? null
      : (editingAd.category.parent || null);

    // Set selected category (parent if available, else leaf)
    setSelectedCategory(parentId || leafId);
    setSelectedSubcategory(leafId);

    // Fetch schema for the leaf category to build dynamic form
    fetchSchema(leafId).catch(() => {});

    // Prefill scalar fields from the ad details
    const prefill: FormData = {
      title: editingAd.title || '',
      description: editingAd.description || '',
      price: editingAd.price || '',
      city: editingAd.city || '',
      location: editingAd.location || '',
      contact_number: (editingAd as any).contact_number || editingAd.seller_phone || '',
      is_price_negotiable: Boolean(editingAd.is_price_negotiable),
      is_urgent: Boolean(editingAd.is_urgent),
      show_contact: (editingAd as any).show_contact ?? true,
    };
    // Vehicle / optional fields when present
    if ((editingAd as any).year != null) prefill.year = (editingAd as any).year as number;
    if ((editingAd as any).mileage != null) prefill.mileage = (editingAd as any).mileage as number;
    if ((editingAd as any).fuel_type) prefill.fuel_type = (editingAd as any).fuel_type as string;
    if ((editingAd as any).transmission) prefill.transmission = (editingAd as any).transmission as string;
    if ((editingAd as any).condition) prefill.condition = (editingAd as any).condition as string;

    setFormData(prefill);
    setErrors({});
    // Images are managed in dedicated edit section
  }, [editId, editingAd, apiCategories.length, fetchSchema]);

  // After schema loads in edit mode, prefill dynamic fields from the ad
  useEffect(() => {
    if (!editId || !editingAd || schemaFields.length === 0) return;

    setFormData(prev => {
      const next: FormData = { ...prev };
      schemaFields.forEach(field => {
        if (field.type === 'file') return;
        const key = field.name as keyof typeof editingAd;
        const existing = (editingAd as any)[key];
        // Only set if we have a value and user hasn't typed anything yet
        const hasUserValue = next[field.name] !== undefined && next[field.name] !== '';
        if (existing !== undefined && existing !== null && !hasUserValue) {
          if (field.type === 'number') {
            const n = Number(existing);
            next[field.name] = Number.isFinite(n) ? n : existing;
          } else if (field.type === 'checkbox') {
            next[field.name] = Boolean(existing);
          } else {
            next[field.name] = existing as any;
          }
        }
      });
      return next;
    });
  }, [editId, editingAd, schemaFields]);

  // Map API categories to CategoryConfig format
  // Form fields are fetched dynamically from /api/categories/<uuid>/schema/
  const CATEGORIES = useMemo((): CategoryConfig[] => {
    if (!apiCategories.length) return [];
    return apiCategories.map(apiCat => {
      const apiChildren = apiCat.subcategories || [];
      const subcategories = apiChildren.length > 0
        ? apiChildren.map(child => ({
          id: child.id,
          label: child.name,
          fields: [] as AdField[], // Fields loaded dynamically from schema API
        }))
        : [];

      return {
        id: apiCat.id,
        label: apiCat.name,
        slug: apiCat.slug,
        icon: apiCat.Icon,
        subcategories,
        commonFields: [], // Fields now come from schema API
      };
    });
  }, [apiCategories]);

  const category = useMemo(() =>
    CATEGORIES.find(c => c.id === selectedCategory),
    [CATEGORIES, selectedCategory]
  );

  const subcategory = useMemo(() =>
    category?.subcategories.find(s => s.id === selectedSubcategory),
    [category, selectedSubcategory]
  );

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return CATEGORIES;
    const search = categorySearch.toLowerCase();
    return CATEGORIES.filter(cat =>
      cat.label.toLowerCase().includes(search)
    );
  }, [CATEGORIES, categorySearch]);

  // Filter subcategories based on search
  const filteredSubcategories = useMemo(() => {
    if (!category) return [];
    if (!subcategorySearch.trim()) return category.subcategories;
    const search = subcategorySearch.toLowerCase();
    return category.subcategories.filter(sub =>
      sub.label.toLowerCase().includes(search)
    );
  }, [category, subcategorySearch]);

  // Use schema fields from API - fully dynamic
  const allFields = useMemo(() => {
    // Need both category and subcategory selected
    if (!selectedCategory || !selectedSubcategory || !category) return [];
    return schemaFields;
  }, [selectedCategory, selectedSubcategory, category, schemaFields]);

  // Helper: find field meta by name
  const getFieldByName = useCallback((name: string): AdField | undefined => {
    return allFields.find(f => f.name === name);
  }, [allFields]);

  // Helper: validate Pakistan mobile number and normalize to digits
  const normalizePakistanMobile = (raw: string): { ok: boolean; normalized: string; message?: string } => {
    const digits = (raw || '').replace(/\D/g, '');
    const ok = /^03\d{9}$/.test(digits);
    return ok
      ? { ok: true, normalized: digits }
      : { ok: false, normalized: digits, message: 'Enter a valid mobile number (e.g., 03XX-XXXXXXX)' };
  };

  const getStepNumber = () => {
    if (!selectedCategory) return 1;
    if (!selectedSubcategory) return 2;
    return 3;
  };

  const handleCategorySelect = (catId: string) => {
    setSelectedCategory(catId);
    setSelectedSubcategory(null);
    setSubcategorySearch('');
    setFormData({});
    setErrors({});
    resetSchema();
  };

  const handleSubcategorySelect = async (subId: string) => {
    setSelectedSubcategory(subId);
    setFormData({});
    setErrors({});
    try {
      await fetchSchema(subId);
    } catch {
      // Error handled in hook
    }
  };

  const handleFieldChange = (name: string, value: string | number | boolean) => {
    // Senior UX: sanitize price input (digits and up to 2 decimals)
    if (name === 'price') {
      let s = String(value ?? '');
      s = s.replace(/[^\d.]/g, '');           // remove non-digits and non-dot
      s = s.replace(/(\..*)\./g, '$1');      // keep only first dot
      const parts = s.split('.');
      if (parts.length > 1) parts[1] = parts[1].slice(0, 2); // max 2 decimals
      s = parts.join('.');
      setFormData(prev => ({ ...prev, [name]: s }));
      const field = getFieldByName(name);
      if (field) {
        const err = validateField(field, s as unknown as number);
        setErrors(prev => ({ ...prev, [name]: err }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    // Real-time validation per field
    const field = getFieldByName(name);
    if (field) {
      const err = validateField(field, value as string | number);
      setErrors(prev => ({ ...prev, [name]: err }));
    } else if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateField = (field: AdField, value: string | number): string => {
    // Required check
    if (field.required && (value === undefined || value === null || value === '')) {
      return `${field.label} is required`;
    }

    const valStr = String(value ?? '').trim();

    // Per-field domain rules
    switch (field.name) {
      case 'title':
        if (valStr && valStr.length < 6) return 'Title must be at least 6 characters';
        // Allow letters + numbers + spaces; disallow titles that are only numbers or only symbols
        if (valStr) {
          const hasLetter = /[A-Za-z]/.test(valStr);
          const onlyDigits = /^\d+$/.test(valStr.replace(/\s+/g, ''));
          const onlySymbols = /^[^A-Za-z0-9]+$/.test(valStr.replace(/\s+/g, ''));
          if (!hasLetter || onlyDigits || onlySymbols) {
            return 'Title must include letters (not only numbers or symbols)';
          }
        }
        break;
      case 'description':
        if (valStr && valStr.length < 20) return 'Description must be at least 20 characters';
        break;
      case 'city':
        if (valStr && valStr.length < 2) return 'Enter a valid city name';
        break;
      case 'location':
        if (valStr && valStr.length < 3) return 'Enter a more specific location';
        break;
      case 'contact_number': {
        if (valStr) {
          const { ok, message } = normalizePakistanMobile(valStr);
          if (!ok) return message || 'Invalid phone number';
        }
        break;
      }
      case 'price': {
        // Accept only digits and optional 1-2 decimals
        if (valStr) {
          const pattern = /^\d+(?:\.\d{1,2})?$/;
          if (!pattern.test(valStr)) return 'Enter a valid amount (digits only, up to 2 decimals)';
          const n = Number(valStr);
          if (!Number.isFinite(n) || n <= 0) return 'Price must be a positive number';
        }
        break;
      }
      case 'mileage': {
        if (valStr) {
          const n = Number(value);
          if (!Number.isFinite(n) || n < 0) return 'Mileage must be zero or a positive number';
        }
        break;
      }
      case 'year': {
        if (valStr) {
          const n = Number(value);
          if (!Number.isFinite(n)) return 'Enter a valid year';
          if (n < 1950 || n > 2026) return 'Year must be between 1950 and 2026';
        }
        break;
      }
      default:
        break;
    }

    // Generic numeric bounds from schema
    if (field.validation && typeof value === 'number') {
      if (field.validation.min !== undefined && value < field.validation.min) {
        return field.validation.message || `${field.label} must be at least ${field.validation.min}`;
      }
      if (field.validation.max !== undefined && value > field.validation.max) {
        return field.validation.message || `${field.label} must be at most ${field.validation.max}`;
      }
    }
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    allFields.forEach(field => {
      // File fields are validated against the `images` state, not `formData`
      if (field.type === 'file') {
        if (field.required && images.length === 0) {
          newErrors.images = 'Please upload at least one photo';
        }
        return;
      }
      const value = formData[field.name];
      const error = validateField(field, value as string | number);
      if (error) {
        newErrors[field.name] = error;
      }
    });
    // Extra guard: phone normalization before submit
    if (!newErrors.contact_number && formData.contact_number !== undefined) {
      const { ok } = normalizePakistanMobile(String(formData.contact_number));
      if (!ok) newErrors.contact_number = 'Enter a valid Pakistani mobile number (e.g., 03XX-XXXXXXX)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = MAX_IMAGES - images.length;
    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    filesToAdd.forEach(file => {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, images: 'Please upload image files only' }));
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setErrors(prev => ({ ...prev, images: `Image size should be less than ${MAX_IMAGE_SIZE_MB}MB` }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const newImage: ImagePreview = {
          id: Math.random().toString(36).substring(7),
          file,
          preview: reader.result as string,
        };
        setImages(prev => [...prev, newImage]);
        if (errors.images) {
          setErrors(prev => ({ ...prev, images: '' }));
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = '';
  }, [images.length, errors.images, MAX_IMAGES, MAX_IMAGE_SIZE_BYTES, MAX_IMAGE_SIZE_MB]);

  const removeImage = useCallback((index: number) => {
    setImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      // Revoke object URL to free memory
      if (prev[index]?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(prev[index].preview);
      }
      return newImages;
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!selectedSubcategory) return;

    // Build payload matching POST /api/ads/create/ spec
    const phoneCheck = normalizePakistanMobile(String(formData.contact_number ?? ''));
    if (!phoneCheck.ok) {
      setErrors(prev => ({ ...prev, contact_number: phoneCheck.message || 'Invalid phone number' }));
      return;
    }

    const adData: CreateAdRequest = {
      title: String(formData.title ?? '').trim(),
      description: String(formData.description ?? '').trim(),
      price: Number(formData.price) || 0,
      category_id: selectedSubcategory, // Backend expects the leaf category UUID
      city: String(formData.city ?? '').trim(),
      location: String(formData.location ?? '').trim(),
      contact_number: phoneCheck.normalized,
      is_price_negotiable: Boolean(formData.is_price_negotiable),
      is_urgent: Boolean(formData.is_urgent),
    };

    // Copy every schema-driven field that has a value, coercing numerics so
    // `year` / `mileage` are sent as numbers (the spec uses int32).
    const NUMERIC_FIELDS = new Set(['year', 'mileage']);
    allFields.forEach(field => {
      if (field.type === 'file') return;
      if (field.name in adData) return;
      const raw = formData[field.name];
      if (raw === undefined || raw === null || raw === '') return;

      if (NUMERIC_FIELDS.has(field.name) || field.type === 'number') {
        const n = Number(raw);
        if (!Number.isNaN(n)) adData[field.name] = n;
      } else {
        adData[field.name] = raw;
      }
    });

    if (editId) {
      // Edit flow: build UpdateAdRequest (no category/image updates here)
      const updatePayload: Record<string, unknown> = {
        title: adData.title,
        description: adData.description,
        price: adData.price,
        category_id: selectedSubcategory,
        city: adData.city,
        location: adData.location,
        contact_number: adData.contact_number,
        is_price_negotiable: adData.is_price_negotiable,
        is_urgent: adData.is_urgent,
        show_contact: (formData.show_contact as boolean) ?? true,
      };
      // Include any extra schema-driven fields present in formData
      allFields.forEach(field => {
        if (field.type === 'file') return;
        if (field.name in updatePayload) return;
        const raw = formData[field.name];
        if (raw === undefined || raw === null || raw === '') return;
        updatePayload[field.name] = raw;
      });

      setIsUpdating(true);
      try {
        const updated = await updateAd(editId, updatePayload as any);
        if (updated) {
          navigate('/profile/my-ads');
        }
      } finally {
        setIsUpdating(false);
      }
      return;
    }

    // Create flow
    const imageFiles = images.map(img => img.file);
    const createdAd = await createAd(adData, imageFiles);

    if (createdAd) {
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }
  };

  const renderField = (field: AdField) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];

    const baseInputClasses = "w-full px-4 py-3 rounded-lg border-2 focus:ring-0 transition-all outline-none text-gray-900 placeholder:text-gray-400";
    const errorClasses = error ? "border-red-500 bg-red-50" : "border-gray-200 bg-white hover:border-primary-300 focus:border-primary-500";

    switch (field.type) {
      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            className={`${baseInputClasses} ${errorClasses}`}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={`${baseInputClasses} ${errorClasses} resize-none`}
          />
        );

      case 'radio':
        return (
          <div className="flex gap-4 flex-wrap">
            {field.options?.map(opt => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name={field.name}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-offset-0"
                />
                <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value as number}
            onChange={(e) => handleFieldChange(field.name, parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder}
            className={`${baseInputClasses} ${errorClasses}`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`${baseInputClasses} ${errorClasses}`}
          />
        );

      default:
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`${baseInputClasses} ${errorClasses}`}
          />
        );
    }
  };

  // Success overlay
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center max-w-md w-full animate-fade-in-up">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ad Posted Successfully!</h2>
          <p className="text-gray-600 mb-4">Your ad has been submitted and will be visible after review.</p>
          <p className="text-sm text-gray-500">Redirecting to home...</p>
        </div>
      </div>
    );
  }

  // Edit mode: show loading state while fetching ad details
  if (editId && isLoadingAd) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex items-center justify-center">
            <div className="flex items-center gap-3 text-gray-600">
              <div className="w-6 h-6 border-2 border-primary-600/30 border-t-primary-600 rounded-full animate-spin" />
              Loading ad details...
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{editId ? 'Edit Ad' : 'Post an Ad'} | AdsWorld</title>
        <meta name="description" content={editId ? 'Edit your ad details' : 'Post your ad and reach thousands of buyers in Pakistan.'} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar mobileOpen={mobileOpen} onToggleMobile={toggleMobile} />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Main Container - All sections in one card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            {/* Progress Steps (hidden in edit mode) */}
            <div className="mb-8">
              {!editId && (
              <div className="flex items-center justify-center gap-2 sm:gap-4">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full font-semibold text-sm sm:text-base transition-all ${step === getStepNumber()
                      ? 'bg-primary-600 text-white scale-110 shadow-lg shadow-primary-500/30'
                      : step < getStepNumber()
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                      }`}>
                      {step < getStepNumber() ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : step}
                    </div>
                    {step < 3 && (
                      <div className={`w-8 sm:w-16 h-1 mx-1 sm:mx-2 rounded-full transition-all ${step < getStepNumber() ? 'bg-green-500' : 'bg-gray-200'
                        }`} />
                    )}
                  </div>
                ))}
              </div>
              )}
              {!editId && (
              <div className="flex justify-center gap-12 md:gap-24 mt-3">
                <span className={`text-xs sm:text-sm font-medium ${getStepNumber() === 1 ? 'text-primary-600' : 'text-gray-400'}`}>Category</span>
                <span className={`text-xs sm:text-sm font-medium ${getStepNumber() === 2 ? 'text-primary-600' : 'text-gray-400'}`}>Type</span>
                <span className={`text-xs sm:text-sm font-medium ${getStepNumber() === 3 ? 'text-primary-600' : 'text-gray-400'}`}>Ad Post</span>
              </div>
              )}
            </div>

            {/* Page Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                {editId ? 'Edit Your Ad' : 'Post Your Ad'}
              </h1>
              <p className="text-gray-600 text-base sm:text-lg">
                {editId ? 'Update details to improve your listing.' : "Reach thousands of buyers across World. It's quick and easy."}
              </p>
            </div>

            {/* Category Selection - Card Grid Style (hidden in edit mode) */}
            {!editId && !selectedCategory && (
              <>
                {/* Header with Icon */}
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-6 h-6 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900">Choose a Category</h2>
                </div>

                {/* Search Input */}
                <div className="mb-6 relative">
                  <input
                    type="text"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder="e.g. Car, Sofa, Bike, Laptop"
                    className="w-full px-4 py-3 pl-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-700 placeholder-gray-400"
                  />
                  <Search className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  {categorySearch && (
                    <button
                      onClick={() => setCategorySearch('')}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Category Grid */}
                {isLoadingCategories ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="w-16 h-16 bg-blue-100 rounded-2xl mx-auto mb-3" />
                        <div className="h-4 w-24 bg-gray-200 rounded mx-auto" />
                      </div>
                    ))}
                  </div>
                ) : filteredCategories.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No categories found matching "{categorySearch}"</p>
                    <button
                      onClick={() => setCategorySearch('')}
                      className="text-primary-600 hover:text-primary-700 mt-2"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {filteredCategories.map(cat => {
                      const Icon = cat.icon;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => handleCategorySelect(cat.id)}
                          className="group relative flex flex-col items-center gap-3 p-4 sm:p-6 rounded-2xl border-2 border-gray-100 hover:border-primary-500 hover:bg-gradient-to-br hover:from-primary-50 hover:to-primary-100 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary-500/10"
                        >
                          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center group-hover:from-primary-500 group-hover:to-primary-600 transition-all duration-300 shadow-md">
                            <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-primary-600 group-hover:text-white transition-colors" />
                          </div>
                          <span className="font-semibold text-gray-900 text-sm sm:text-base text-center group-hover:text-primary-700 transition-colors">
                            {cat.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* Subcategory Selection */}
            {!editId && selectedCategory && !selectedSubcategory && category && (
              <div className="animate-fade-in">
                {/* Header with Back */}
                <div className="flex items-center gap-2 mb-6">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <Sparkles className="w-6 h-6 text-primary-600" />
                  <h2 className="text-xl font-bold text-gray-900">{category.label}</h2>
                </div>

                {/* Search Input */}
                <div className="mb-6 relative">
                  <input
                    type="text"
                    value={subcategorySearch}
                    onChange={(e) => setSubcategorySearch(e.target.value)}
                    placeholder={`Search in ${category.label}...`}
                    className="w-full px-4 py-3 pl-11 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-700 placeholder-gray-400"
                  />
                  <Search className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  {subcategorySearch && (
                    <button
                      onClick={() => setSubcategorySearch('')}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Subcategory Grid */}
                {filteredSubcategories.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No types found matching "{subcategorySearch}"</p>
                    <button
                      onClick={() => setSubcategorySearch('')}
                      className="text-primary-600 hover:text-primary-700 mt-2"
                    >
                      Clear search
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {filteredSubcategories.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => handleSubcategorySelect(sub.id)}
                      className="group flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all duration-200 bg-white text-left"
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {sub.label}
                      </span>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
                    </button>
                  ))}
                  </div>
                )}
              </div>
            )}

            {/* Form */}
            {(editId ? Boolean(selectedSubcategory) : (selectedCategory && selectedSubcategory && Boolean(category))) && (
              <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
                {/* Form Header */}
                <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
                  {/* API Error Banner */}
                  {apiError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700">{apiError}</p>
                    </div>
                  )}

                  {!editId && (
                    <button
                      type="button"
                      onClick={() => setSelectedSubcategory(null)}
                      className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors group"
                    >
                      <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                      <span className="font-medium">Back</span>
                    </button>
                  )}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center">
                      {category?.icon && <category.icon className="w-6 h-6 text-primary-600" />}
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{editId ? 'Edit Ad Details' : (subcategory?.label || 'Post Ad')}</h2>
                      <p className="text-gray-500 text-sm">{editId ? 'Update the fields and save changes.' : 'Fill in the details below'}</p>
                    </div>
                  </div>

                  {/* Image Upload (disabled in edit mode here) */}
                    {!editId && (
                    <div className="mb-8">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                        <ImageIcon className="w-4 h-4 text-primary-600" />
                        Add Photos
                        <span className="text-gray-400 font-normal">(up to {MAX_IMAGES}, max {MAX_IMAGE_SIZE_MB}MB each)</span>
                      </label>
                      <div className="flex flex-wrap gap-3 sm:gap-4">
                        {images.map((img, i) => (
                          <div key={img.id} className="relative w-20 h-20 sm:w-24 sm:h-24 group">
                            <img src={img.preview} alt={`Upload ${i + 1}`} className="w-full h-full object-cover rounded-xl shadow-md" />
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              className="absolute -top-2 -right-2 w-6 h-6 sm:w-7 sm:h-7 bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow-lg hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <X className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                            {i === 0 && (
                              <div className="absolute bottom-1 left-1 bg-primary-600 text-white text-[10px] px-1.5 py-0.5 rounded-md font-medium">
                                Cover
                              </div>
                            )}
                          </div>
                        ))}
                        {images.length < MAX_IMAGES && (
                          <label className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1.5 hover:border-primary-500 hover:bg-primary-50 transition-all hover:scale-105 cursor-pointer">
                            <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                            <span className="text-[10px] sm:text-xs text-gray-500 font-medium">Add Photo</span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              className="hidden"
                              onChange={handleImageUpload}
                            />
                          </label>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 mt-2">First photo will be your cover image</p>
                      {errors.images && (
                        <p className="text-xs sm:text-sm text-red-500 mt-1.5 flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errors.images}
                        </p>
                      )}
                    </div>
                  )}

                  {editId && editingAd && (
                    <div className="mb-8">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                        <ImageIcon className="w-4 h-4 text-primary-600" />
                        Photos
                        <span className="text-gray-400 font-normal">(first photo is the cover)</span>
                      </label>
                      <div className="flex flex-wrap gap-3 sm:gap-4">
                        {editingAd.images?.map((url, i) => (
                          <div key={url} className="relative w-20 h-20 sm:w-24 sm:h-24 group">
                            <img src={url} alt={`Ad ${i + 1}`} className="w-full h-full object-cover rounded-xl shadow-md" />
                            {editingAd.primary_image === url && (
                              <div className="absolute bottom-1 left-1 bg-primary-600 text-white text-[10px] px-1.5 py-0.5 rounded-md font-medium">
                                Cover
                              </div>
                            )}
                            {/* Optional: delete button if backend supports */}
                            <button
                              type="button"
                              onClick={async () => {
                                if (!editId) return;
                                try {
                                  await adService.deleteAdImage(editId, encodeURIComponent(url));
                                  await refetchAd();
                                } catch (e) {
                                  console.error('Failed to delete image', e);
                                }
                              }}
                              className="absolute -top-2 -right-2 w-6 h-6 sm:w-7 sm:h-7 bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow-lg hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                              aria-label="Remove image"
                            >
                              <X className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        ))}
                        {/* Upload new images */}
                        <label className="w-20 h-20 sm:w-24 sm:h-24 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1.5 hover:border-primary-500 hover:bg-primary-50 transition-all hover:scale-105 cursor-pointer">
                          <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                          <span className="text-[10px] sm:text-xs text-gray-500 font-medium">Add Photo</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={async (e) => {
                              const files = e.target.files ? Array.from(e.target.files) : [];
                              if (!files.length || !editId) return;
                              try {
                                await adService.uploadAdImages(editId, files);
                                await refetchAd();
                              } catch (e) {
                                console.error('Failed to upload images', e);
                              } finally {
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Dynamic Fields */}
                  <div className="space-y-4 sm:space-y-5">
                    {isLoadingSchema && (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-2 border-primary-600/30 border-t-primary-600 rounded-full animate-spin" />
                        <span className="ml-3 text-gray-600">Loading form fields...</span>
                      </div>
                    )}
                    {!isLoadingSchema && schemaError && (
                      <div className="text-center py-8 text-red-500">
                        <p>Error loading form fields.</p>
                        <p className="text-sm mt-1">{schemaError}</p>
                      </div>
                    )}
                    {!isLoadingSchema && !schemaError && allFields.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No fields available for this category.</p>
                        <p className="text-sm mt-1">Please try selecting a different subcategory.</p>
                      </div>
                    )}
                    {allFields
                      // Image/file fields are handled by the dedicated uploader above
                      .filter(field => field.type !== 'file')
                      .map(field => {
                        // Checkboxes have a different layout (label next to the input)
                        if (field.type === 'checkbox') {
                          return (
                            <div key={field.name}>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={Boolean(formData[field.name])}
                                  onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 focus:ring-offset-0"
                                />
                                <span>{field.label}</span>
                                {field.required && <span className="text-red-500">*</span>}
                              </label>
                              {!errors[field.name] && field.helpText && (
                                <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div key={field.name}>
                            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                              {field.label}
                              {field.required && <span className="text-red-500">*</span>}
                            </label>
                            {renderField(field)}
                            {!errors[field.name] && field.helpText && (
                              <p className="text-xs sm:text-sm text-gray-500 mt-1.5">{field.helpText}</p>
                            )}
                            {errors[field.name] && (
                              <p className="text-xs sm:text-sm text-red-500 mt-1.5 flex items-center gap-1.5">
                                <AlertCircle className="w-3.5 h-3.5" />
                                {errors[field.name]}
                              </p>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Submit Buttons - Sticky on mobile */}
                <div className="sticky bottom-0 bg-white/95 backdrop-blur-lg rounded-t-3xl shadow-2xl p-4 sm:p-6 border-t border-gray-100">
                  <div className="flex gap-3 sm:gap-4 max-w-5xl mx-auto">
                    {!editId && (
                      <button
                        type="button"
                        onClick={() => setSelectedSubcategory(null)}
                        className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={editId ? isUpdating : isSubmitting}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg shadow-primary-500/30 disabled:opacity-70 flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                      {(editId ? isUpdating : isSubmitting) ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          {editId ? 'Saving...' : 'Posting...'}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          {editId ? 'Save Changes' : 'Post Ad'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out;
        }
      `}</style>
    </>
  );
}
